import { PHYSICS_HZ } from '../config';
import type { PhysicsStats } from '../shared/protocol';
import type { SceneInfo } from '../render/scene';
import { errors } from './errors';

export interface OverlayState {
  fps: number;
  stats: PhysicsStats | null;
  scene: SceneInfo | null;
  isolated: boolean;
  transfer: 'SharedArrayBuffer' | 'postMessage' | '—';
  simdModule: boolean;
  simdBrowser: boolean;
  nodes: number;
  beams: number;
  sabBytes: number;
  unstableBeam: number;
  unstableNode: number;
  eco: boolean;
}

const fmtMB = (b: number) => (b / 1048576).toFixed(1) + ' MB';

export class DebugOverlay {
  private el: HTMLElement;
  private last = 0;

  constructor(el: HTMLElement) {
    this.el = el;
  }

  setVisible(v: boolean): void {
    this.el.hidden = !v;
  }

  get visible(): boolean {
    return !this.el.hidden;
  }

  /** Estimare: memoria WASM + bufferele partajate + bufferele GPU create de noi + heap-ul JS dacă browserul îl raportează. */
  static memoryEstimate(s: OverlayState): number {
    const heap = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory?.usedJSHeapSize ?? 0;
    return (s.stats?.wasmBytes ?? 0) + s.sabBytes + (s.scene?.gpuBytes ?? 0) + heap;
  }

  update(s: OverlayState, now: number): void {
    if (this.el.hidden || now - this.last < 250) return;
    this.last = now;
    const st = s.stats;
    const ok = (b: boolean) => (b ? '<b class="ok">da</b>' : '<b class="bad">nu</b>');
    const sps = st ? Math.round(st.stepsPerSecond) : 0;
    const rows = [
      `FPS <b>${s.fps.toFixed(0)}</b>${s.eco ? ' (economie)' : ''}`,
      `Fizică <b>${sps}</b> pași/s din ${PHYSICS_HZ}${st?.slowMotion ? ' <b class="bad">ÎNCETINIT</b>' : ''}`,
      `Timp/pas <b>${st ? st.microsPerStep.toFixed(1) : '–'} µs</b>`,
      `Timp simulat <b>${st ? st.simTime.toFixed(1) : '–'} s</b>`,
      `Memorie ≈ <b>${fmtMB(DebugOverlay.memoryEstimate(s))}</b> (WASM ${fmtMB(st?.wasmBytes ?? 0)})`,
      `crossOriginIsolated ${ok(s.isolated)} · transfer <b>${s.transfer}</b>`,
      `WASM SIMD: modul ${ok(s.simdModule)} · browser ${ok(s.simdBrowser)}`,
      `Randare <b>${s.scene?.backend ?? '–'}</b> · ${s.scene?.calls ?? 0} draw calls · ${s.scene?.triangles ?? 0} triunghiuri`,
      `Noduri ${s.nodes} · grinzi ${s.beams} · rupte ${st?.brokenBeams ?? 0}`
    ];
    if (s.unstableBeam >= 0) rows.push(`<b class="bad">Grinda ${s.unstableBeam} e instabilă la 2000 Hz</b>`);
    if (s.unstableNode >= 0) rows.push(`<b class="bad">Nodul ${s.unstableNode} e instabil la 2000 Hz</b>`);
    const errs = errors();
    if (errs.length) {
      rows.push(`<b class="bad">Erori: ${errs.length}</b>`);
      for (const e of errs.slice(-3)) rows.push(`<span class="err">${escapeHtml(e.message)}</span>`);
    }
    this.el.innerHTML = rows.map((r) => `<div>${r}</div>`).join('');
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}
